import { useEffect, useState } from 'react';

export type PresenceState = 'active' | 'idle' | 'logged-out';

export const PRESENCE_IDLE_MS = 10 * 60 * 1000;

const ACTIVITY_EVENTS = [
  'keydown', 'mousedown', 'mousemove', 'pointerdown', 'touchstart', 'wheel', 'scroll',
] as const;

/**
 * Browser-local presence only. Authentication remains server authoritative.
 * Activity is never persisted or sent off the device.
 */
export function usePresence(
  authenticated: boolean,
  navigationKey: string,
  idleMs = PRESENCE_IDLE_MS,
): PresenceState {
  const [presence, setPresence] = useState<PresenceState>(
    authenticated ? 'active' : 'logged-out',
  );

  useEffect(() => {
    if (!authenticated) {
      setPresence('logged-out');
      return;
    }

    let lastActivity = Date.now();
    let timer = 0;

    const scheduleIdle = () => {
      window.clearTimeout(timer);
      const remaining = Math.max(0, idleMs - (Date.now() - lastActivity));
      timer = window.setTimeout(() => setPresence('idle'), remaining);
    };

    const recordActivity = () => {
      lastActivity = Date.now();
      setPresence('active');
      scheduleIdle();
    };

    recordActivity();
    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, recordActivity, { capture: true, passive: true });
    }
    window.addEventListener('popstate', recordActivity, { passive: true });
    window.addEventListener('hashchange', recordActivity, { passive: true });

    return () => {
      window.clearTimeout(timer);
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, recordActivity, { capture: true });
      }
      window.removeEventListener('popstate', recordActivity);
      window.removeEventListener('hashchange', recordActivity);
    };
  }, [authenticated, navigationKey, idleMs]);

  return presence;
}
